<template>
	<div class="student-place-shared-files">
		<div class="title-container">
			<h1>Fichiers partagés</h1>
		</div>

		<div v-if="loading" class="loading">
			<div class="spinner"></div>
			<p>Chargement des fichiers...</p>
		</div>

		<div v-else-if="error" class="error">
			<img src="/img/error.svg" alt="Erreur" />
			<p>{{ error }}</p>
		</div>

		<div v-else-if="sharedFiles.length === 0" class="empty-state">
			<img src="/img/file-types/file.svg" alt="Aucun fichier" />
			<h3>Aucun fichier partagé</h3>
			<p>Votre professeur n'a encore partagé aucun fichier avec votre classe.</p>
		</div>

		<div v-else class="files-grid">
			<div
				v-for="file in sharedFiles"
				:key="file.id"
				class="file-card"
				@click="downloadFile(file)"
			>
				<div class="file-icon">
					<img
						:src="getFileIcon(file.name)"
						:alt="file.name"
					/>
				</div>
				<div class="file-info">
					<h3 class="file-name">{{ file.name }}</h3>
					<p class="file-size">{{ formatFileSize(file.size) }}</p>
					<p class="file-date">Partagé le {{ formatDate(file.sharedAt) }}</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { sharedFilesForClassService, sharedFilesForStudentService } from "../../services/filesService";
import { downloadMedia } from "../../services/mediaService";
import { getFileLogo } from "../../utils/fileTypes";
import { decrypt } from "../../utils/utils";

export default {
	name: "StudentPlaceSharedFiles",
	data() {
		return {
			sharedFiles: [],
			loading: true,
			error: null
		};
	},
	async mounted() {
		await this.loadSharedFiles();
	},
	methods: {
		async loadSharedFiles() {
			try {
				this.loading = true;
				this.error = null;
				
				let files = [];
				
				// Vérifier si c'est un étudiant connecté
				if (localStorage.getItem('userId') === null) {
					// Pour un étudiant, utiliser sharedFilesForStudentService
					files = await sharedFilesForStudentService();
				} else {
					// Pour un professeur, utiliser sharedFilesForClassService avec l'ID de classe
					const classId = decrypt(this.$route.params.classId);
					
					if (!classId) {
						this.error = "Impossible de récupérer l'ID de classe.";
						return;
					}
					
					files = await sharedFilesForClassService(classId);
				}
				console.log(files)
				this.sharedFiles = files.map(file => ({
					id: file.id,
					name: file.name,
					size: file.size || 0,
					sharedAt: file.uploaded_at || file.shared_at,
					file_upload: file.file_upload,
					teacher: file.teacher
				}));
				
			} catch (error) {
				console.error("Erreur lors du chargement des fichiers partagés:", error);
				this.error = "Impossible de charger les fichiers partagés. Veuillez réessayer plus tard.";
			} finally {
				this.loading = false;
			}
		},
		
		async downloadFile(file) {
			console.log(file)

			try {
				let filePath;
				
				// Générer le chemin du fichier selon le type d'utilisateur
				if (localStorage.getItem('userId') === null) {
					// Pour un étudiant, utiliser generateFileUrl
					filePath = file.file_upload
					await downloadMedia(filePath, file.name, true);

				} else {
					// Pour un professeur, utiliser le chemin direct si disponible
					filePath = file.file_upload
					await downloadMedia(filePath, file.name, false);
				}
				
				this.$store.state.toast = {
					status: 'success',
					text: 'Fichier téléchargé avec succès',
				};
			} catch (error) {
				console.error("Erreur lors du téléchargement:", error);
				this.$store.state.toast = {
					status: 'error',
					text: "Erreur lors du téléchargement du fichier"
				};
			}
		},
		
		getFileIcon(fileName) {
			return getFileLogo(fileName);
		},
		
		formatFileSize(bytes) {
			if (bytes === 0) return '0 Bytes';
			
			const k = 1024;
			const sizes = ['Bytes', 'KB', 'MB', 'GB'];
			const i = Math.floor(Math.log(bytes) / Math.log(k));
			
			return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
		},
		
		formatDate(dateString) {
			if (!dateString) return 'Date inconnue';
			
			const date = new Date(dateString);
			
			// Vérifier si la date est valide
			if (isNaN(date.getTime())) {
				return 'Date invalide';
			}
			
			return date.toLocaleDateString('fr-FR', {
				year: 'numeric',
				month: 'long',
				day: 'numeric'
			});
		}
	}
};
</script>

<style lang="scss" scoped>
.student-place-shared-files {
	padding: 20px;
	max-width: 1200px;
	margin: 0 auto;

	.loading {
		text-align: center;
		padding: 60px 20px;

		.spinner {
			width: 40px;
			height: 40px;
			border: 4px solid #ecf0f1;
			border-top: 4px solid #3498db;
			border-radius: 50%;
			animation: spin 1s linear infinite;
			margin: 0 auto 20px;
		}

		p {
			color: #7f8c8d;
			font-size: 1.1rem;
		}
	}

	.error {
		text-align: center;
		padding: 60px 20px;

		img {
			width: 80px;
			height: 80px;
			margin-bottom: 20px;
		}

		p {
			color: #e74c3c;
			font-size: 1.1rem;
		}
	}

	.empty-state {
		text-align: center;
		padding: 60px 20px;

		img {
			width: 100px;
			height: 100px;
			opacity: 0.5;
			margin-bottom: 20px;
		}

		h3 {
			color: #2c3e50;
			font-size: 1.5rem;
			margin-bottom: 10px;
		}

		p {
			color: #7f8c8d;
			font-size: 1.1rem;
		}
	}

	.files-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
		gap: 20px;
		margin-top: 40px;

		.file-card {
			background: white;
			border-radius: 12px;
			box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
			padding: 20px;
			cursor: pointer;
			transition: all 0.3s ease;
			position: relative;

			&:hover {
				transform: translateY(-5px);
				box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
			}

			.file-icon {
				text-align: center;
				margin-bottom: 15px;

				img {
					width: 60px;
					height: 60px;
				}
			}

			.file-info {
				text-align: center;
				margin-bottom: 15px;

				.file-name {
					color: #2c3e50;
					font-size: 1.1rem;
					font-weight: 600;
					margin-bottom: 5px;
					word-break: break-word;
				}

				.file-size {
					color: #7f8c8d;
					font-size: 0.9rem;
					margin-bottom: 3px;
				}

				.file-date {
					color: #95a5a6;
					font-size: 0.8rem;
				}
			}

			.download-btn {
				position: absolute;
				top: 15px;
				right: 15px;
				background: #3498db;
				border: none;
				border-radius: 50%;
				width: 35px;
				height: 35px;
				cursor: pointer;
				transition: background-color 0.3s ease;
				display: flex;
				align-items: center;
				justify-content: center;

				&:hover {
					background: #2980b9;
				}

				img {
					width: 18px;
					height: 18px;
					filter: brightness(0) invert(1);
				}
			}
		}
	}
}

@keyframes spin {
	0% { transform: rotate(0deg); }
	100% { transform: rotate(360deg); }
}

// Responsive design
@media (max-width: 768px) {
	.student-place-shared-files {
		padding: 15px;

		.files-grid {
			grid-template-columns: 1fr;
			gap: 15px;
		}
	}
}
</style>
